import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Button,
  Typography,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';

import { IHeaderTopBar } from '../../../models/IResponsiveTopBar';
import assets from '../../../assets';
import { getCollections } from '../../../api/CollectionAPI';
import { ICollection } from '../../../models/ICollection';
import colorConfigs from '../../../configs/colorConfigs';
import { typo } from '../../../assets/fonts/font';
import { PATH_NAME } from '../../../configs/pathName';
import HeaderStyles from '../../../styles/PublicPage/header';
import { useCart } from '../../../context/CartContext';

const ResponsiveHeader: React.FC<IHeaderTopBar> = ({ isMobile }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [collections, setCollections] = useState<ICollection[]>([]);
  const [shopAllOpen, setShopAllOpen] = useState(false);
  const isCartEnabled = process.env.REACT_APP_ENABLE_CART === 'true';

  const navigate = useNavigate();
  const location = useLocation();
  const { totalQuantity } = useCart();

  useEffect(() => {
    setUserToken(localStorage.getItem('token'));

    const fetchCollections = async () => {
      try {
        const response = await getCollections();
        setCollections(response);
      } catch (error) {
        console.error('Failed to fetch collections', error);
      }
    };

    fetchCollections();
  }, []);

  const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const navItems = [
    {
      label: 'All Products',
      children: collections.map((collection) => ({
        label: collection.name,
        path: `/collections/${collection.slug}`,
      })),
    },
    { label: 'Locations', path: PATH_NAME.LOCATIONS },
    { label: 'Smart Fridge', path: PATH_NAME.SMART_FRIDGE },
    {
      label: userToken ? 'My Account' : 'Login',
      path: userToken ? PATH_NAME.MEMBER : PATH_NAME.LOGIN,
    },
  ];

  const isActive = (path: string) => !!matchPath(path, location.pathname);

  return (
    <>
      {/* Top Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Drawer Toggle */}
        <IconButton onClick={toggleDrawer(true)}>
          <MenuIcon sx={{ color: 'var(--color-black)' }} />
        </IconButton>

        {/* Centered Logo */}
        <Box
          onClick={() => handleNavigation(PATH_NAME.ROOT)}
          sx={{ cursor: 'pointer' }}
        >
          <img
            src={assets.images.logo}
            alt="Logo"
            style={{ height: '20px' }}
          />
        </Box>

        {isCartEnabled && (
          <IconButton onClick={() => navigate(PATH_NAME.CART)}>
            <Badge
              badgeContent={totalQuantity}
              color="primary"
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: colorConfigs.primary,
                  color: "var(--color-white)",
                },
                color: "var(--color-black)",
              }}
            >
              <ShoppingCartOutlinedIcon sx={{ fontSize: "2rem" }} />
            </Badge>
          </IconButton>
        )}
      </Box>

      {/* Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            width: 250,
            height: "100%",
            background: colorConfigs.primary,
            color: "var(--color-white)",
          }}
          role="presentation"
        >
          <List>
            {/* Menu Items */}
            {navItems.map((item, index) =>
              !item.children ? (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    ...typo.sideBar,
                    backgroundColor: location.pathname === item.path
                      ? colorConfigs.primary
                      : colorConfigs.primary,
                    '&:hover': { backgroundColor: colorConfigs.primaryHover },
                    cursor: 'pointer',
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ sx: { ...typo.sideBar } }}
                  />
                </ListItem>
              ) : (
                <React.Fragment key={index}>
                  <ListItem
                    button
                    onClick={() => setShopAllOpen((prev) => !prev)}
                    sx={{
                      ...typo.sideBar,
                      '&:hover': { backgroundColor: colorConfigs.primaryHover },
                      cursor: 'pointer',
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ sx: { ...typo.sideBar } }}
                    />
                    {shopAllOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItem>
                  <Collapse in={shopAllOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child, childIndex) => (
                        <ListItem
                          button
                          key={childIndex}
                          onClick={() => handleNavigation(child.path)}
                          sx={{
                            pl: 4,
                            ...typo.sideBar,
                            backgroundColor:
                              location.pathname === child.path
                                ? colorConfigs.tertiary
                                : colorConfigs.primary,
                            borderLeft:
                              location.pathname === child.path
                                ? `4px solid ${colorConfigs.secondary}`
                                : 'none',
                            '&:hover': {
                              backgroundColor: colorConfigs.tertiary,
                            },
                          }}
                        >
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{ sx: { ...typo.sideBar } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              )
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default ResponsiveHeader;
